/**
 * Ordered, bounded-memory transport for one growing MediaRecorder byte stream.
 *
 * The module talks directly to a server-created private Google Cloud Storage
 * resumable session. The upload URL is a bearer secret: consumers must keep it
 * in memory and must never log or persist it. Storage's committed Range is the
 * source of truth after every partial or ambiguous request.
 */

/** Google Cloud Storage resumable requests require 256 KiB block alignment. */
export const RESUMABLE_ALIGNMENT = 256 * 1024;
/** Non-final uploads are emitted in 8 MiB blocks to balance latency and overhead. */
export const RESUMABLE_BLOCK_SIZE = 8 * 1024 * 1024;
/** Pending bytes above this threshold are moved from memory to IndexedDB. */
export const DEFAULT_MEMORY_LIMIT = 24 * 1024 * 1024;
/** MediaRecorder emits recoverable data approximately once per second. */
export const RECORDER_TIMESLICE_MS = 1000;

export const RECORDING_MIME_TYPES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4;codecs=avc1,mp4a.40.2',
  'video/mp4',
];

/**
 * Select the first recorder MIME type supported by the current browser.
 * @param {typeof MediaRecorder} MediaRecorderImpl Injectable recorder for tests.
 * @returns {string} The actual MIME type to pass to MediaRecorder.
 * @throws {Error} When MediaRecorder or all approved codecs are unavailable.
 */
export function negotiateRecordingMimeType(MediaRecorderImpl = window.MediaRecorder) {
  if (!MediaRecorderImpl) throw new Error('MediaRecorder is unavailable in this browser.');
  const selected = RECORDING_MIME_TYPES.find((type) => MediaRecorderImpl.isTypeSupported?.(type));
  if (!selected) throw new Error('This browser does not expose a supported interview recording codec.');
  return selected;
}

/**
 * Select one recording quality tier before capture begins.
 *
 * Network Information is advisory and unavailable in some browsers, so the
 * normal tier is the safe default. The tier never changes mid-recording.
 * @returns {{name: string, width: number, height: number, frameRate: number,
 * videoBitsPerSecond: number, audioBitsPerSecond: number}}
 */
export function selectCaptureTier({ connection = navigator.connection, userAgent = navigator.userAgent } = {}) {
  const effectiveType = String(connection?.effectiveType || '').toLowerCase();
  const saveData = Boolean(connection?.saveData);
  const mobile = /android|iphone|ipad|ipod|mobile/i.test(String(userAgent || ''));
  if (saveData || mobile || ['slow-2g', '2g', '3g'].includes(effectiveType)) {
    return {
      name: 'constrained', width: 854, height: 480, frameRate: 18,
      videoBitsPerSecond: 750000, audioBitsPerSecond: 56000,
    };
  }
  if (effectiveType === '4g' && Number(connection?.downlink || 0) >= 8 && !mobile) {
    return {
      name: 'strong', width: 1280, height: 720, frameRate: 24,
      videoBitsPerSecond: 1550000, audioBitsPerSecond: 80000,
    };
  }
  return {
    name: 'normal', width: 960, height: 540, frameRate: 22,
    videoBitsPerSecond: 1050000, audioBitsPerSecond: 64000,
  };
}

/**
 * Build the single camera/microphone consent request for a capture tier.
 * @param {object} tier Result from {@link selectCaptureTier}.
 * @returns {MediaStreamConstraints} Browser capture constraints.
 */
export function captureConstraints(tier) {
  return {
    video: {
      width: { ideal: tier.width, max: tier.width },
      height: { ideal: tier.height, max: tier.height },
      frameRate: { ideal: tier.frameRate, max: Math.max(tier.frameRate, 24) },
    },
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  };
}

/**
 * Convert the selected tier and MIME type into MediaRecorder options.
 * @param {string} mimeType Browser-supported recorder type.
 * @param {object} tier Selected capture tier.
 * @returns {MediaRecorderOptions}
 */
export function recorderOptions(mimeType, tier) {
  return {
    mimeType,
    videoBitsPerSecond: tier.videoBitsPerSecond,
    audioBitsPerSecond: tier.audioBitsPerSecond,
  };
}

/**
 * Convert Storage's inclusive `Range: bytes=0-N` into the next byte offset.
 * Missing or malformed Range headers mean that no bytes are known committed.
 * @param {string|null} value Range response header.
 * @returns {number} Exclusive committed offset.
 */
export function parseCommittedRange(value) {
  const match = /^bytes=0-(\d+)$/i.exec(String(value || '').trim());
  return match ? Number(match[1]) + 1 : 0;
}

/** Resolve after a retry/backoff delay. Injectable callers may replace it. */
function wait(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/** Read a Blob in modern and legacy browser implementations. */
function blobArrayBuffer(blob) {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Could not read a recording segment.'));
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * Session-scoped persistence for unacknowledged recording segments.
 *
 * Rows are keyed by session and absolute start offset. Only bytes not yet
 * acknowledged by Storage are stored, and `clearSession` never touches another
 * interview's recovery data.
 */
export class IndexedDbRecordingStore {
  /** Create a spill store for one server-owned interview session. */
  constructor(sessionId, indexedDBImpl = window.indexedDB) {
    this.sessionId = sessionId;
    this.indexedDB = indexedDBImpl;
    this.dbPromise = null;
  }

  /** Open or reuse the versioned recovery database and segment object store. */
  async db() {
    if (!this.indexedDB) throw new Error('IndexedDB is unavailable for recording recovery.');
    if (this.dbPromise) return this.dbPromise;
    this.dbPromise = new Promise((resolve, reject) => {
      const request = this.indexedDB.open('jobseeqr-ai-interview-recordings', 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains('segments')) {
          request.result.createObjectStore('segments', { keyPath: 'key' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error || new Error('Could not open recording recovery storage.'));
    });
    return this.dbPromise;
  }

  /** Persist one byte segment and return its session-scoped key. */
  async put(start, data) {
    const database = await this.db();
    const key = `${this.sessionId}:${start}`;
    await new Promise((resolve, reject) => {
      const transaction = database.transaction('segments', 'readwrite');
      transaction.objectStore('segments').put({ key, sessionId: this.sessionId, start, data });
      transaction.oncomplete = resolve;
      transaction.onerror = () => reject(transaction.error);
    });
    return key;
  }

  /** Read a previously spilled segment, or null when it no longer exists. */
  async get(key) {
    const database = await this.db();
    return new Promise((resolve, reject) => {
      const request = database.transaction('segments', 'readonly').objectStore('segments').get(key);
      request.onsuccess = () => resolve(request.result?.data || null);
      request.onerror = () => reject(request.error);
    });
  }

  /** Delete one acknowledged spill row; a null key is intentionally a no-op. */
  async delete(key) {
    if (!key) return;
    const database = await this.db();
    await new Promise((resolve, reject) => {
      const transaction = database.transaction('segments', 'readwrite');
      transaction.objectStore('segments').delete(key);
      transaction.oncomplete = resolve;
      transaction.onerror = () => reject(transaction.error);
    });
  }

  /** Remove all recovery rows owned by this session after success or abort. */
  async clearSession() {
    const database = await this.db();
    const rows = await new Promise((resolve, reject) => {
      const request = database.transaction('segments', 'readonly').objectStore('segments').getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
    await Promise.all(rows.filter((row) => row.sessionId === this.sessionId).map((row) => this.delete(row.key)));
  }
}

/**
 * Sequential resumable uploader for a live, append-only recorder stream.
 *
 * Public calls are serialized through `chain`; callers may enqueue new blobs
 * while prior blocks upload. `bytesAcknowledged` always reflects Storage's
 * authoritative committed offset, never an optimistic local assumption.
 */
export class ResumableRecordingUploader {
  /**
   * Construct an uploader around a server-issued resumable capability.
   * @param {object} options Dependency-injectable transport and recovery policy.
   */
  constructor({
    uploadUrl,
    contentType,
    sessionId,
    fetchImpl = window.fetch.bind(window),
    spillStore = null,
    memoryLimit = DEFAULT_MEMORY_LIMIT,
    maxRetries = 5,
    sleep = wait,
    onMetrics = () => {},
  }) {
    if (!uploadUrl) throw new Error('A resumable upload URL is required.');
    this.uploadUrl = uploadUrl;
    this.contentType = contentType;
    this.fetch = fetchImpl;
    this.spillStore = spillStore || new IndexedDbRecordingStore(sessionId);
    this.memoryLimit = memoryLimit;
    this.maxRetries = maxRetries;
    this.sleep = sleep;
    this.onMetrics = onMetrics;
    this.segments = [];
    this.bytesProduced = 0;
    this.bytesAcknowledged = 0;
    this.retryCount = 0;
    this.oldestPendingAt = 0;
    this.chain = Promise.resolve();
    this.finalized = false;
    this.finalizePromise = null;
  }

  /** Return immutable operational counters suitable for UI warnings/telemetry. */
  metrics() {
    return {
      bytesProduced: this.bytesProduced,
      bytesAcknowledged: this.bytesAcknowledged,
      pendingBytes: this.bytesProduced - this.bytesAcknowledged,
      oldestPendingAge: this.oldestPendingAt ? Math.max(0, Date.now() - this.oldestPendingAt) : 0,
      retryCount: this.retryCount,
      lastCommittedOffset: this.bytesAcknowledged,
    };
  }

  /** Publish the current metric snapshot without exposing byte contents or URL. */
  emitMetrics() {
    this.onMetrics(this.metrics());
  }

  /**
   * Append one timed MediaRecorder Blob to the logical stream.
   * @returns {Promise<void>} Resolves after queuing, spill checks, and any ready
   * intermediate blocks have completed in order.
   */
  enqueue(blob) {
    if (this.finalized) return Promise.reject(new Error('Recording upload is already finalized.'));
    const start = this.bytesProduced;
    const expectedSize = Number(blob.size || 0);
    if (!expectedSize) return Promise.resolve();
    this.bytesProduced += expectedSize;
    if (!this.oldestPendingAt) this.oldestPendingAt = Date.now();
    this.emitMetrics();
    const shouldSpillOnRead = this.bytesProduced - this.bytesAcknowledged > this.memoryLimit;
    const prepared = blobArrayBuffer(blob).then(async (buffer) => {
      const data = new Uint8Array(buffer);
      if (data.byteLength !== expectedSize) throw new Error('A recording segment changed size while being queued.');
      const segment = { start, end: start + data.byteLength, data, spillKey: null, createdAt: Date.now() };
      if (shouldSpillOnRead) {
        segment.spillKey = await this.spillStore.put(start, data);
        segment.data = null;
      }
      return segment;
    });
    this.chain = this.chain.then(async () => {
      this.segments.push(await prepared);
      await this.spillIfNeeded();
      await this.flushIntermediate();
      this.emitMetrics();
    });
    return this.chain;
  }

  /** Count only segment bytes currently retained in JavaScript memory. */
  memoryBytes() {
    return this.segments.reduce((total, segment) => total + (segment.data?.byteLength || 0), 0);
  }

  /** Spill oldest in-memory segments until the configured bound is restored. */
  async spillIfNeeded() {
    let memory = this.memoryBytes();
    if (memory <= this.memoryLimit) return;
    for (const segment of this.segments) {
      if (memory <= this.memoryLimit || !segment.data) break;
      segment.spillKey = await this.spillStore.put(segment.start, segment.data);
      memory -= segment.data.byteLength;
      segment.data = null;
    }
  }

  /** Resolve a segment's bytes from memory or its IndexedDB spill row. */
  async segmentData(segment) {
    if (segment.data) return segment.data;
    const data = await this.spillStore.get(segment.spillKey);
    if (!data) throw new Error('A recoverable recording segment is missing.');
    return data instanceof Uint8Array ? data : new Uint8Array(data);
  }

  /** Assemble up to `length` pending bytes starting at the committed offset. */
  async readPending(length) {
    let needed = Math.min(length, this.bytesProduced - this.bytesAcknowledged);
    const parts = [];
    for (const segment of this.segments) {
      if (needed <= 0) break;
      if (segment.end <= this.bytesAcknowledged) continue;
      const data = await this.segmentData(segment);
      const localStart = Math.max(0, this.bytesAcknowledged - segment.start);
      const take = Math.min(needed, data.byteLength - localStart);
      parts.push(data.slice(localStart, localStart + take));
      needed -= take;
    }
    const size = parts.reduce((total, part) => total + part.byteLength, 0);
    if (!size) throw new Error('No pending recording bytes are available.');
    return new Blob(parts, { type: this.contentType });
  }

  /**
   * Advance to Storage's exclusive committed offset and delete committed data.
   * Rejects backward, non-finite, or beyond-produced offsets because accepting
   * them could silently duplicate or skip recording bytes.
   */
  async acknowledge(offset) {
    if (!Number.isFinite(offset) || offset < this.bytesAcknowledged || offset > this.bytesProduced) {
      throw new Error('Cloud Storage returned an invalid committed recording offset.');
    }
    this.bytesAcknowledged = offset;
    const next = [];
    for (const segment of this.segments) {
      if (segment.end <= offset) {
        await this.spillStore.delete(segment.spillKey);
        continue;
      }
      if (segment.start < offset) {
        const data = await this.segmentData(segment);
        const remaining = data.slice(offset - segment.start);
        await this.spillStore.delete(segment.spillKey);
        next.push({ ...segment, start: offset, data: remaining, spillKey: null });
      } else {
        next.push(segment);
      }
    }
    this.segments = next;
    this.oldestPendingAt = next.length ? Math.min(...next.map((item) => item.createdAt)) : 0;
    await this.spillIfNeeded();
    this.emitMetrics();
  }

  /** Query Storage after an ambiguous request without sending recording bytes. */
  async queryCommittedOffset() {
    const response = await this.fetch(this.uploadUrl, {
      method: 'PUT',
      // The browser owns Content-Length. Setting that forbidden header can
      // itself turn an otherwise recoverable status query into a network error.
      headers: { 'Content-Range': 'bytes */*' },
      body: new Blob([]),
    });
    if (response.status === 200 || response.status === 201) return this.bytesProduced;
    if (response.status !== 308) throw new Error(`Could not recover recording upload offset (HTTP ${response.status}).`);
    return parseCommittedRange(response.headers.get('Range'));
  }

  /**
   * Send one ordered block, recover partial commits, and retry safely.
   * @param {Blob} blob Bytes beginning exactly at `bytesAcknowledged`.
   * @param {number|'*'} total Known final total or `*` while recording grows.
   */
  async sendBlock(blob, total = '*') {
    const start = this.bytesAcknowledged;
    const end = start + blob.size - 1;
    let lastError;
    for (let attempt = 0; attempt <= this.maxRetries; attempt += 1) {
      try {
        const response = await this.fetch(this.uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': this.contentType,
            'Content-Range': `bytes ${start}-${end}/${total}`,
          },
          body: blob,
        });
        if (response.status === 200 || response.status === 201) {
          await this.acknowledge(end + 1);
          return response;
        }
        if (response.status === 308) {
          const range = response.headers.get('Range');
          let committed = parseCommittedRange(range);
          if (!range) committed = await this.queryCommittedOffset();
          if (committed <= start) {
            lastError = new Error('Cloud Storage did not commit the attempted recording block.');
            if (attempt < this.maxRetries) {
              this.retryCount += 1;
              this.emitMetrics();
              await this.sleep(Math.min(8000, 250 * (2 ** attempt)));
              continue;
            }
            break;
          }
          await this.acknowledge(committed);
          return response;
        }
        if (response.status === 0) {
          throw new Error('The private recording upload could not reach Cloud Storage. The interview can continue and this technical issue does not affect your evaluation.');
        }
        if (response.status < 500 && response.status !== 408 && response.status !== 429) {
          throw new Error(`Recording upload was rejected (HTTP ${response.status}).`);
        }
        lastError = new Error(`Retryable recording upload failure (HTTP ${response.status}).`);
      } catch (error) {
        lastError = error;
        try {
          const committed = await this.queryCommittedOffset();
          await this.acknowledge(committed);
          if (committed > start) return null;
        } catch (_) {
          // The original request and the status query were both ambiguous.
        }
      }
      if (attempt < this.maxRetries) {
        this.retryCount += 1;
        this.emitMetrics();
        await this.sleep(Math.min(8000, 250 * (2 ** attempt)));
      }
    }
    throw lastError || new Error('Recording upload failed after retrying.');
  }

  /** Upload every complete non-final block while retaining one final candidate. */
  async flushIntermediate() {
    // Keep one block as the potential final block. A recording whose size is
    // an exact 8 MiB multiple still needs a request carrying the known total.
    while (this.bytesProduced - this.bytesAcknowledged > RESUMABLE_BLOCK_SIZE) {
      const blob = await this.readPending(RESUMABLE_BLOCK_SIZE);
      if (blob.size % RESUMABLE_ALIGNMENT !== 0) {
        throw new Error('Non-final recording blocks must be aligned to 256 KiB.');
      }
      await this.sendBlock(blob, '*');
    }
  }

  /** Return the single shared finalization promise for idempotent callers. */
  finalize() {
    if (this.finalizePromise) return this.finalizePromise;
    this.finalizePromise = this.finalizeOnce();
    return this.finalizePromise;
  }

  /** Flush the final known-total request and remove all acknowledged spill data. */
  async finalizeOnce() {
    this.finalized = true;
    await this.chain;
    await this.flushIntermediate();
    const pending = this.bytesProduced - this.bytesAcknowledged;
    if (pending <= 0) {
      await this.spillStore.clearSession();
      return this.metrics();
    }
    const finalBlob = await this.readPending(pending);
    const response = await this.sendBlock(finalBlob, this.bytesProduced);
    if (response && response.status !== 200 && response.status !== 201) {
      const committed = await this.queryCommittedOffset();
      await this.acknowledge(committed);
    }
    if (this.bytesAcknowledged !== this.bytesProduced) {
      throw new Error('Cloud Storage did not acknowledge the complete recording.');
    }
    await this.spillStore.clearSession();
    return this.metrics();
  }
}

/**
 * Request the final recorder data, stop, and resolve only after the stop event.
 * MediaRecorder dispatches its final `dataavailable` before `stop`, allowing the
 * caller's existing data listener to enqueue the last bytes first.
 * @param {MediaRecorder|null} recorder Active browser recorder.
 * @returns {Promise<void>}
 */
export function stopMediaRecorderAndWait(recorder) {
  if (!recorder || recorder.state === 'inactive') return Promise.resolve();
  return new Promise((resolve, reject) => {
    const onStop = () => {
      recorder.removeEventListener?.('error', onError);
      resolve();
    };
    const onError = (event) => reject(event?.error || new Error('MediaRecorder failed while stopping.'));
    recorder.addEventListener?.('stop', onStop, { once: true });
    recorder.addEventListener?.('error', onError, { once: true });
    try {
      recorder.requestData?.();
      recorder.stop();
    } catch (error) {
      reject(error);
    }
  });
}
